/**
 * Date processing functions
 */

import { ymd } from './date-utils.js';
import { escRx } from './date-utils.js';

// Days per month (Feb=29 to allow leap-year inputs when year is unknown)
const MAX_DAYS_PER_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Validate calendar date: rejects 31.02, 30.02, and Feb 29 in non-leap years when year is known.
function isValidDate(day, month, year) {
    if (!Number.isInteger(day) || !Number.isInteger(month)) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1) return false;
    if (year === null || year === undefined) {
        // Year unknown: allow Feb 29 (could be leap year), reject day > max-per-month
        return day <= MAX_DAYS_PER_MONTH[month - 1];
    }
    if (!Number.isInteger(year)) return false;
    // Leap-year aware: cross-check against Date roll-over
    const d = new Date(Date.UTC(year, month - 1, day));
    return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

/**
 * Process recognized date
 * Validates calendar correctness (day-in-month + leap year) before processing.
 * Mutates state object.
 */
export function processResult(day, month, year, matchedStr, addAsVariant, state) {
    if (isValidDate(day, month, year)) {
        if (year) {
            const dateStr = ymd(year, month, day);
            if (addAsVariant) {
                // Add as variant instead of primary date
                // For ambiguous dates, use exact date pattern (not \d{4}) so both interpretations are in OR condition
                state.dateVariants.push({
                    from: dateStr,
                    to: dateStr,
                    type: 'ambiguous',
                    pattern: dateStr // Exact date pattern for OR condition
                });
            } else {
                // Set as primary date (only if not already set)
                if (!state.dateFrom) {
                    state.dateFrom = dateStr;
                    state.dateTo = dateStr;
                }
            }
        } else {
            // No year - create variant for all years
            state.dateVariants.push({
                from: `1900-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
                to: `2099-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
                type: 'day-month',
                pattern: `\\d{4}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
            });
        }
        
        // Track which tokens were used
        // For compact format (6 digits without separators), extract individual parts
        const matchedTrimmed = matchedStr.trim();
        if (/^\d{6}$/.test(matchedTrimmed)) {
            const digits = matchedTrimmed;
            state.usedTokens = [
                digits.substring(0, 2), // day or year
                digits.substring(2, 4), // month
                digits.substring(4, 6)   // year or day
            ];
        } else {
            // Check if this is a numeric date (no text month) or text date (has month name)
            const monthMatch = matchedStr.match(/([a-zа-яё]+)/iu);
            
            if (monthMatch) {
                // Text date: extract month name and ALL numbers (day and year)
                const tokens = [];
                // Extract ALL numbers from the matched string
                const allNumbers = matchedStr.match(/\d+/g) || [];
                // Month name (text)
                tokens.push(monthMatch[1]);
                // Add all numbers (day, year, etc.)
                allNumbers.forEach(num => tokens.push(num));
                state.usedTokens = tokens.length > 0 ? tokens : matchedStr.split(/[\s\-\.,/]+/).filter(Boolean);
            } else {
                // Numeric date with separators (e.g., "53.12.11", "15-11-72")
                // Extract ALL numeric components to ensure month is included
                const numericParts = matchedStr.match(/\d+/g);
                if (numericParts && numericParts.length > 0) {
                    // Use all numeric parts (e.g., "53.12.11" → ["53", "12", "11"])
                    state.usedTokens = numericParts;
                } else {
                    // Fallback: split by separators
                    state.usedTokens = matchedStr.split(/[\s\-\.,/]+/).filter(Boolean);
                }
            }
        }
        // Remove the date part from query (but keep other words)
        // Use the full matched string (including trailing separator) for proper removal
        const datePatternEscaped = matchedStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Also try removing with trailing separator
        state.stripped = state.q.replace(new RegExp(datePatternEscaped + '\\s*', 'iu'), '').replace(/\s+/g, ' ').trim();
    }
}


/**
 * Process month-year pattern (without day)
 * Creates variant for all days of the month
 * Mutates state object
 */
export function processMonthYear(month, year, matchedStr, addAsVariant, state) {
    if (month >= 1 && month <= 12 && year) {
        const pattern = `${year}-${String(month).padStart(2, '0')}-\\d{2}`;
        const monthYearVariant = {
            from: `${year}-${String(month).padStart(2, '0')}-01`,
            to: `${year}-${String(month).padStart(2, '0')}-31`,
            type: 'month-year',
            pattern: pattern
        };
        
        if (addAsVariant) {
            state.dateVariants.push(monthYearVariant);
        } else if (!state.dateFrom) {
            state.dateFrom = monthYearVariant.from;
            state.dateTo = monthYearVariant.to;
        }
        
        // Track tokens
        const tokens = [];
        const monthMatch = matchedStr.match(/([a-zа-яё]+)/iu);
        if (monthMatch) tokens.push(monthMatch[1]);
        const yearMatch = matchedStr.match(/(\d{2,4})/);
        if (yearMatch) tokens.push(yearMatch[1]);
        state.usedTokens = tokens.length > 0 ? tokens : matchedStr.split(/[\s\-\.,/]+/).filter(Boolean);
        
        // Remove from query
        const datePatternEscaped = matchedStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        state.stripped = state.q.replace(new RegExp(datePatternEscaped + '\\s*', 'iu'), '').replace(/\s+/g, ' ').trim();
    }
}

